import ActivityIcon from '@clearkit/icons/glyphs/Clock';
import SegmentsIcon from '@clearkit/icons/glyphs/Segment';
import PersonIcon from '@clearkit/icons/glyphs/User';
import { CKAvatar, CKDrawer, CKTab, CKTabList, CKTabs } from 'clearkit';
import some from 'lodash/some';
import { Helmet } from 'react-helmet';

import AttributeBrowser from '~/components/AttributeBrowser';
import GraphqlError from '~/components/GraphqlError';
import wrapError from '~/components/JavascriptError/JavascriptErrorHandler';
import LoadingArea from '~/components/LoadingArea';
import MissingPage from '~/components/MissingPage';
import { useFancyQuery } from '~/lib/graphql';
import { paginateData } from '~/lib/graphql/pagination';

import Actions from './Actions';
import Activity from './Activity';
import PersonDetails from './Details';
import query from './query.graphql';
import Segments from './Segments';

const Person = ({ id }) => {
  const {
    data,
    data: { person, salesforceAccountConnector, hubspotAccountConnector } = {},
    error,
    loading,
    refetch,
  } = useFancyQuery(query, {
    variables: { id },
    returnPartialData: true,
  });

  const [tabIndex, setTabIndex] = useState(0);
  const overflowScrollRef = React.useRef();

  const handleTabChange = (stickyHeaderBounds) => {
    const stickyHeaderGap = 28;
    const customOffset =
      stickyHeaderBounds.top - stickyHeaderBounds.height - stickyHeaderGap;
    if (overflowScrollRef.current) {
      overflowScrollRef.current.scrollTo({ top: customOffset });
    }
  };

  if (error) {
    return (
      <div className="x-sidebar">
        <GraphqlError error={error} refetch={refetch} />;
      </div>
    );
  }

  if (!loading && !person && !error)
    return <MissingPage className="x-sidebar" />;

  if ((loading && !data) || !person)
    return <LoadingArea loading className="x-sidebar" />;

  const {
    salesforceAccounts,
    salesforceContacts,
    salesforceLeads,
    hubspotCompanies,
  } = paginateData(person, {
    fields: [
      'salesforceAccounts',
      'salesforceContacts',
      'salesforceLeads',
      'hubspotCompanies',
    ],
  });

  return (
    <div className="x-sidebar">
      <Helmet title={person.name} />
      <CKDrawer.Header className="border-none">
        <div className="flex items-center flex-1">
          <div className="mr-4">
            <CKAvatar src={person.avatar} size={56} className="shadow-sm" />
          </div>

          <div>
            <CKDrawer.Heading>{person.name || 'Loading...'}</CKDrawer.Heading>
            {person.email ? (
              <a
                href={`mailto://${person.email}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue">
                {person.email}
              </a>
            ) : (
              <span>&mdash;</span>
            )}
          </div>

          <div className="ml-auto">
            <Actions
              person={person}
              hubspotOwners={hubspotAccountConnector?.owners}
              salesforceAccounts={salesforceAccounts}
              salesforceAccountConnector={salesforceAccountConnector}
              salesforceContacts={salesforceContacts}
              salesforceLeads={salesforceLeads}
              hubspotCompanies={hubspotCompanies}
            />
          </div>
        </div>
      </CKDrawer.Header>

      <CKTabs
        aria-label="Person tabs"
        className="flex flex-col flex-grow min-h-0"
        isBordered
        index={tabIndex}
        onChange={setTabIndex}>
        <CKTabList
          className="flex-shrink-0 px-10 -mt-4"
          aria-label="Person tabs">
          <CKTab icon={<PersonIcon />}>Person details</CKTab>
          <CKTab icon={<ActivityIcon />}>Events</CKTab>
          <CKTab icon={<SegmentsIcon />}>Segments</CKTab>
        </CKTabList>

        {tabIndex === 0 && (
          <div className="overflow-y-auto" ref={overflowScrollRef}>
            <PersonDetails person={person} />

            <LoadingArea loading={!some([person.traits, person.enrichment])}>
              <AttributeBrowser
                onTabChange={handleTabChange}
                object={person}
                traits={person.traits}
                enrichment={person.enrichment}
                hubspotOwners={hubspotAccountConnector?.owners}
                hubspotCompanies={hubspotCompanies}
                salesforceLeads={salesforceLeads}
                salesforceContacts={salesforceContacts}
                salesforceAccounts={salesforceAccounts}
                salesforceAccountConnector={salesforceAccountConnector}
              />
            </LoadingArea>
          </div>
        )}
        {tabIndex === 1 && (
          <Activity person={person} className="flex flex-col flex-grow" />
        )}
        {tabIndex === 2 && (
          <Segments person={person} className="flex flex-col flex-grow" />
        )}
      </CKTabs>
    </div>
  );
};

Person.propTypes = {
  id: PropTypes.string.isRequired,
};

export default wrapError(Person);
